import { useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useGetBatteriesQuery, useGetBatteryHistoryQuery } from '../../../redux/api/batteriesApi';
import { ScooterBatteryAction } from '../../../resources/types/batteriesTypes';
import { TableCell as MuiTableCell } from '@mui/material';
import moment from 'moment';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)'
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.1)'
  }`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  padding: '12px 16px'
}));

const HistoryTab = () => {
  const { t } = useTranslation();
  const [selectedBatteryId, setSelectedBatteryId] = useState<string>('');

  const { data: batteriesData } = useGetBatteriesQuery({ limit: 1000 });
  const { data: historyData, isLoading } = useGetBatteryHistoryQuery(
    { scooterBattery: selectedBatteryId },
    { skip: !selectedBatteryId }
  );

  const getActionLabel = (action: ScooterBatteryAction) => {
    const labels = {
      [ScooterBatteryAction.CHARGING_END]: t('batteries.actionChargingEnd'),
      [ScooterBatteryAction.REMOVED_FROM_SCOOTER_FOR_CHARGING]: t('batteries.actionRemovedForCharging'),
      [ScooterBatteryAction.REMOVED_FROM_SCOOTER_FOR_MAINTENANCE]: t('batteries.actionRemovedForMaintenance'),
      [ScooterBatteryAction.INSTALLED_TO_SCOOTER_AFTER_CHARGING]: t('batteries.actionInstalledAfterCharging'),
      [ScooterBatteryAction.INSTALLED_TO_SCOOTER_AFTER_MAINTENANCE]: t('batteries.actionInstalledAfterMaintenance')
    };
    return labels[action];
  };

  const getActionColor = (action: ScooterBatteryAction) => {
    switch (action) {
      case ScooterBatteryAction.CHARGING_END:
        return 'success';
      case ScooterBatteryAction.REMOVED_FROM_SCOOTER_FOR_CHARGING:
        return 'info';
      case ScooterBatteryAction.REMOVED_FROM_SCOOTER_FOR_MAINTENANCE:
        return 'warning';
      case ScooterBatteryAction.INSTALLED_TO_SCOOTER_AFTER_CHARGING:
        return 'success';
      case ScooterBatteryAction.INSTALLED_TO_SCOOTER_AFTER_MAINTENANCE:
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box mb={3}>
        <FormControl fullWidth sx={{ maxWidth: 400 }}>
          <InputLabel>{t('batteries.selectBatteryLabel')}</InputLabel>
          <Select
            value={selectedBatteryId}
            onChange={e => setSelectedBatteryId(e.target.value)}
            label={t('batteries.selectBatteryLabel')}
          >
            <MenuItem value="">
              <em>{t('batteries.notSelected')}</em>
            </MenuItem>
            {batteriesData?.data.map(battery => (
              <MenuItem key={battery._id} value={battery._id}>
                {battery.name} ({battery.code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!selectedBatteryId ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="body1" color="textSecondary">
            {t('batteries.selectBatteryPrompt')}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : !historyData?.data || historyData.data.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="body1" color="textSecondary">
                {t('batteries.noHistory')}
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t('batteries.colDate')}</TableHeaderCell>
                  <TableHeaderCell>{t('batteries.colAction')}</TableHeaderCell>
                  <TableHeaderCell>{t('batteries.colScooter')}</TableHeaderCell>
                  <TableHeaderCell>{t('batteries.colChargingStation')}</TableHeaderCell>
                  <TableHeaderCell>{t('batteries.colBatteryLevel')}</TableHeaderCell>
                  <TableHeaderCell>{t('batteries.colAdmin')}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.data.map(item => (
                  <StyledTableRow key={item._id}>
                    <StyledTableCell>
                      {moment(item.createdAt).format('DD.MM.YYYY HH:mm')}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={getActionLabel(item.action)}
                        color={getActionColor(item.action)}
                        size="small"
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      {item.scooter ? item.scooter.name : '-'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {item.chargingStation ? item.chargingStation.name : '-'}
                    </StyledTableCell>
                    <StyledTableCell>{item.batteryLevel}%</StyledTableCell>
                    <StyledTableCell>{item.adminLogin}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default HistoryTab;
